import { ArrowPathIcon, PlusIcon, TrashIcon, XMarkIcon } from '@heroicons/react/24/outline'
import { useState } from 'react'

import ConfirmModal from '@/components/ConfirmModal'

import InfoTip from './InfoTip'
import { MockLicense, MockLicenseStatus, MOCK_LICENSES } from './mockData'
import { useProviderSession } from './previewSession'

interface InviteReport {
    invited: string[]
    reinvited: string[]
    skippedExisting: string[]
    skippedQuota: string[]
    invalid: string[]
    seatsUsed: number
    seatQuota: number
}

const SEAT_QUOTA = 50

const STATUS_LABEL: Record<MockLicenseStatus, { label: string; cls: string }> = {
    active: { label: 'Active', cls: 'bg-green-100 text-green-800' },
    invited: { label: 'Invited', cls: 'bg-amber-100 text-amber-800' },
    expired: { label: 'Expired', cls: 'bg-neutral-200 text-neutral-700' },
    revoked: { label: 'Revoked', cls: 'bg-red-100 text-red-700' },
}

const FILTERS: { id: 'all' | MockLicenseStatus; label: string }[] = [
    { id: 'all', label: 'All' },
    { id: 'active', label: 'Active' },
    { id: 'invited', label: 'Invited' },
    { id: 'expired', label: 'Expired' },
    { id: 'revoked', label: 'Revoked' },
]

function formatDate(value?: string) {
    if (!value) return null
    const d = new Date(value)
    if (isNaN(d.valueOf())) return null
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function seatsInUse(licenses: MockLicense[]): number {
    return licenses.filter((l) => l.status === 'active' || l.status === 'invited').length
}

export default function ProviderLicenses() {
    const session = useProviderSession()
    // Admins and coordinators can invite, resend, and revoke licenses; the
    // viewer role is read-only everywhere.
    const canManage = session?.role === 'admin' || session?.role === 'coordinator'
    const [licenses, setLicenses] = useState<MockLicense[]>(MOCK_LICENSES)
    const [filter, setFilter] = useState<'all' | MockLicenseStatus>('all')
    const [showAssign, setShowAssign] = useState(false)
    const [revokeId, setRevokeId] = useState<string | null>(null)

    const filtered = filter === 'all' ? licenses : licenses.filter((l) => l.status === filter)
    const seatsUsed = seatsInUse(licenses)
    const seatsRemaining = Math.max(0, SEAT_QUOTA - seatsUsed)
    const activeCount = licenses.filter((l) => l.status === 'active').length
    const invitedCount = licenses.filter((l) => l.status === 'invited').length

    function handleResend(licenseId: string) {
        setLicenses((prev) =>
            prev.map((l) => (l._id === licenseId ? { ...l, invitedAt: new Date().toISOString() } : l))
        )
    }

    function handleRevoke(licenseId: string) {
        setLicenses((prev) => prev.map((l) => (l._id === licenseId ? { ...l, status: 'revoked' } : l)))
        setRevokeId(null)
    }

    function handleInvite(emails: string[]): InviteReport {
        const existing = new Set(licenses.map((l) => l.email.toLowerCase()))
        const invited: string[] = []
        const reinvited: string[] = []
        const skippedExisting: string[] = []
        const invalid: string[] = []
        const additions: MockLicense[] = []

        for (const email of emails) {
            const lower = email.toLowerCase()
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
                invalid.push(email)
                continue
            }
            if (existing.has(lower)) {
                skippedExisting.push(email)
                continue
            }
            existing.add(lower)
            additions.push({
                _id: `inv_new_${additions.length}_${Date.now()}`,
                email,
                status: 'invited',
                registered: false,
                invitedAt: new Date().toISOString(),
            })
            invited.push(email)
        }

        const next = [...licenses, ...additions]
        setLicenses(next)
        return {
            invited,
            reinvited,
            skippedExisting,
            skippedQuota: [],
            invalid,
            seatsUsed: seatsInUse(next),
            seatQuota: SEAT_QUOTA,
        }
    }

    return (
        <div className='max-w-6xl mx-auto px-4 sm:px-6 py-8'>
            <div className='flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3 mb-6'>
                <div>
                    <h1 className='text-2xl sm:text-3xl font-bold text-neutral-900'>Licenses</h1>
                    <p className='text-sm text-neutral-600 mt-1'>
                        {seatsUsed} of {SEAT_QUOTA} seats assigned
                        <InfoTip text='A seat is used as soon as an invitation is sent, even before the client registers. Active seats belong to clients who have created their account and appear on your Clients list; pending seats are invitations not yet accepted.' />
                        {' '}({activeCount} active, {invitedCount} pending invitation{invitedCount === 1 ? '' : 's'}).{' '}
                        <span className='text-neutral-900 font-medium'>{seatsRemaining} remaining.</span>
                    </p>
                </div>
                {canManage && (
                    <div className='flex items-center gap-2'>
                        <button
                            onClick={() => setShowAssign(true)}
                            className='inline-flex items-center gap-2 px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg transition-colors'
                        >
                            <PlusIcon className='w-4 h-4' />
                            Assign license
                        </button>
                    </div>
                )}
            </div>

            {/* Filter tabs */}
            <div className='flex items-center gap-1 mb-4 border-b border-neutral-200 overflow-x-auto'>
                {FILTERS.map((f) => {
                    const count = f.id === 'all' ? licenses.length : licenses.filter((l) => l.status === f.id).length
                    const isActive = filter === f.id
                    return (
                        <button
                            key={f.id}
                            onClick={() => setFilter(f.id)}
                            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px whitespace-nowrap transition-colors ${
                                isActive
                                    ? 'border-brand-600 text-brand-700'
                                    : 'border-transparent text-neutral-600 hover:text-neutral-900'
                            }`}
                        >
                            {f.label} <span className='text-neutral-400 ml-1'>({count})</span>
                        </button>
                    )
                })}
            </div>

            <div className='bg-white border border-neutral-200 rounded-xl overflow-hidden'>
                <div className='overflow-x-auto'>
                    <table className='w-full text-sm'>
                        <thead className='bg-neutral-50 text-neutral-600 text-xs uppercase tracking-wider'>
                            <tr>
                                <th className='px-4 py-3 text-left font-medium'>Email</th>
                                <th className='px-4 py-3 text-left font-medium'>Status</th>
                                <th className='px-4 py-3 text-left font-medium'>Invited</th>
                                <th className='px-4 py-3 text-left font-medium'>Last active</th>
                                <th className='px-4 py-3 text-right font-medium'>Actions</th>
                            </tr>
                        </thead>
                        <tbody className='divide-y divide-neutral-100'>
                            {filtered.map((lic) => (
                                <tr key={lic._id} className='hover:bg-neutral-50/50'>
                                    <td className='px-4 py-3 text-neutral-900'>{lic.email}</td>
                                    <td className='px-4 py-3'>
                                        <span
                                            className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_LABEL[lic.status].cls}`}
                                        >
                                            {STATUS_LABEL[lic.status].label}
                                        </span>
                                    </td>
                                    <td className='px-4 py-3 text-neutral-600'>{formatDate(lic.invitedAt) ?? '—'}</td>
                                    <td className='px-4 py-3 text-neutral-600'>{formatDate(lic.lastActiveAt) ?? 'Never'}</td>
                                    <td className='px-4 py-3'>
                                        <div className='flex items-center justify-end gap-1'>
                                            {canManage && lic.status === 'invited' && (
                                                <button
                                                    title='Resend invite'
                                                    onClick={() => handleResend(lic._id)}
                                                    className='p-1.5 text-neutral-500 hover:text-brand-600 hover:bg-brand-50 rounded'
                                                >
                                                    <ArrowPathIcon className='w-4 h-4' />
                                                </button>
                                            )}
                                            {canManage && lic.status !== 'revoked' && (
                                                <button
                                                    title='Revoke'
                                                    onClick={() => setRevokeId(lic._id)}
                                                    className='p-1.5 text-neutral-500 hover:text-red-600 hover:bg-red-50 rounded'
                                                >
                                                    <TrashIcon className='w-4 h-4' />
                                                </button>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                {filtered.length === 0 && (
                    <div className='py-12 text-center text-sm text-neutral-500'>
                        {licenses.length === 0
                            ? 'No licenses yet. Assign one to invite a client.'
                            : 'No licenses match this filter.'}
                    </div>
                )}
            </div>

            {showAssign && (
                <AssignLicenseModal
                    seatsRemaining={seatsRemaining}
                    seatQuota={SEAT_QUOTA}
                    onClose={() => setShowAssign(false)}
                    onInvite={handleInvite}
                />
            )}

            <ConfirmModal
                open={!!revokeId}
                onCancel={() => setRevokeId(null)}
                onConfirm={async () => {
                    if (revokeId) handleRevoke(revokeId)
                }}
                title='Revoke license'
                description={
                    licenses.find((l) => l._id === revokeId)?.status === 'active'
                        ? 'Revoking frees the seat. The client loses sponsored access and will receive an email letting them know their sponsorship has ended. You can re-invite the same email later, which will consume a seat again.'
                        : 'Revoking frees the seat and quietly withdraws the invitation; no email is sent. You can re-invite the same email later, which will consume a seat again.'
                }
                confirmText='Revoke'
                cancelText='Cancel'
                intent='danger'
            />
        </div>
    )
}

function AssignLicenseModal({
    seatsRemaining,
    seatQuota,
    onClose,
    onInvite,
}: {
    seatsRemaining: number
    seatQuota: number
    onClose: () => void
    onInvite: (emails: string[]) => InviteReport
}) {
    const session = useProviderSession()
    const [text, setText] = useState('')
    const [error, setError] = useState<string | null>(null)
    const [report, setReport] = useState<InviteReport | null>(null)

    function parseEmails(raw: string): string[] {
        return raw
            .split(/[\r\n,;]+/)
            .map((s) => s.trim())
            .filter(Boolean)
    }

    function handleSubmit() {
        setError(null)
        const emails = parseEmails(text)
        if (emails.length === 0) {
            setError('Enter at least one email address')
            return
        }
        setReport(onInvite(emails))
    }

    return (
        <div className='fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/50' onClick={onClose}>
            <div className='bg-white rounded-2xl shadow-xl w-full max-w-md p-6' onClick={(e) => e.stopPropagation()}>
                <div className='flex items-center justify-between mb-4'>
                    <h2 className='text-lg font-semibold text-neutral-900'>Assign a license</h2>
                    <button onClick={onClose} className='p-1 text-neutral-400 hover:text-neutral-700'>
                        <XMarkIcon className='w-5 h-5' />
                    </button>
                </div>

                {report ? (
                    <InviteReportView report={report} onDone={onClose} />
                ) : (
                    <>
                        <p className='text-sm text-neutral-600 mb-4'>
                            Enter one or more email addresses. Each client receives an invitation email; when they
                            register with that email, their license activates automatically.
                            {session?.role === 'coordinator' && ' Clients you invite are added to your caseload.'}
                        </p>

                        <label className='block text-sm font-medium text-neutral-700 mb-1.5'>Email addresses</label>
                        <textarea
                            rows={5}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            placeholder={'parent1@example.com\nparent2@example.com'}
                            className='w-full px-3 py-2 border border-neutral-300 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500'
                        />
                        <p className='text-xs text-neutral-500 mt-1'>One email per line, comma-separated, or paste a list.</p>

                        <div className='mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg text-xs text-blue-900'>
                            Each new invitation uses 1 seat. {seatsRemaining} seats remaining of {seatQuota}.
                        </div>

                        {error && (
                            <div className='mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800'>
                                {error}
                            </div>
                        )}

                        <div className='flex items-center justify-end gap-2 mt-6'>
                            <button onClick={onClose} className='px-4 py-2 text-sm font-medium text-neutral-700 hover:text-neutral-900'>
                                Cancel
                            </button>
                            <button
                                onClick={handleSubmit}
                                className='px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg'
                            >
                                Send invitations
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    )
}

function InviteReportView({ report, onDone }: { report: InviteReport; onDone: () => void }) {
    const sections: { label: string; emails: string[]; cls: string }[] = [
        { label: 'Invited', emails: report.invited, cls: 'text-green-800' },
        { label: 'Re-invited (previously revoked)', emails: report.reinvited, cls: 'text-green-800' },
        { label: 'Already a coordinator or client on this org (skipped)', emails: report.skippedExisting, cls: 'text-amber-800' },
        { label: 'Skipped: over seat quota', emails: report.skippedQuota, cls: 'text-amber-800' },
        { label: 'Invalid emails', emails: report.invalid, cls: 'text-red-800' },
    ].filter((s) => s.emails.length > 0)

    return (
        <>
            <p className='text-sm text-neutral-600 mb-4'>
                {report.seatsUsed} of {report.seatQuota} seats now in use.
            </p>
            <div className='space-y-3 max-h-72 overflow-y-auto'>
                {sections.length === 0 && (
                    <div className='p-3 bg-neutral-50 border border-neutral-200 rounded-lg text-sm text-neutral-700'>
                        No emails were processed.
                    </div>
                )}
                {sections.map((s) => (
                    <div key={s.label}>
                        <div className={`text-xs font-semibold uppercase tracking-wider mb-1 ${s.cls}`}>
                            {s.label} ({s.emails.length})
                        </div>
                        <ul className='text-sm text-neutral-700 space-y-0.5'>
                            {s.emails.map((e) => (
                                <li key={e} className='font-mono'>
                                    {e}
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
            <div className='flex items-center justify-end gap-2 mt-6'>
                <button
                    onClick={onDone}
                    className='px-4 py-2 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium rounded-lg'
                >
                    Done
                </button>
            </div>
        </>
    )
}
